import { Routes, Route, Link, useLocation } from "react-router-dom";
import { Title2, Body1, Button, Spinner, makeStyles, tokens } from "@fluentui/react-components";
import { useUser } from "./lib/user";
import { BrandHome, BrandLogo, BrandFooter } from "./components/Brand";
import Landing from "./pages/Landing";
import Whoami from "./pages/Whoami";
import Admin from "./pages/Admin";
import DashboardViewer from "./pages/DashboardViewer";
import Forbidden from "./pages/Forbidden";
import NotFound from "./pages/NotFound";

const useStyles = makeStyles({
  root: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: tokens.spacingHorizontalXXL,
    minHeight: "100%",
    boxSizing: "border-box"
  },
  viewerRoot: {
    maxWidth: "100%",
    margin: "0",
    padding: "0",
    height: "100vh",
    display: "flex",
    flexDirection: "column"
  },
  signInScreen: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalXXXL,
    textAlign: "center"
  },
  signInTagline: {
    maxWidth: "26rem",
    color: tokens.colorNeutralForeground2
  },
  nav: {
    display: "flex",
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalXL,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    alignItems: "center"
  },
  navLink: {
    textDecoration: "none",
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold
  },
  navDivider: {
    width: "1px",
    alignSelf: "stretch",
    background: tokens.colorNeutralStroke2,
    margin: `0 ${tokens.spacingHorizontalXS}`
  }
});

export default function App() {
  const styles = useStyles();
  const location = useLocation();
  const user = useUser();
  const isInternal = !!user && user.userRoles.includes("internal");
  const isViewer = location.pathname.startsWith("/dashboard/");

  // Dashboard viewer routes use a full-bleed layout so the QuickSight iframe
  // gets the full viewport. Outer chrome is stripped.
  if (isViewer) {
    return (
      <div className={styles.viewerRoot}>
        <Routes>
          <Route path="/dashboard/:clientId/:dashboardId" element={<DashboardViewer />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    );
  }

  if (user === undefined) {
    return (
      <div className={styles.root}>
        <div className={styles.signInScreen}>
          <BrandLogo height={40} />
          <Spinner size="medium" label="Loading…" />
        </div>
      </div>
    );
  }

  if (user === null) {
    return (
      <div className={styles.root}>
        <div className={styles.signInScreen}>
          <BrandLogo height={48} />
          <Title2>Client Reporting</Title2>
          <Body1 className={styles.signInTagline}>
            Sign in with your Microsoft account to view your dashboards.
          </Body1>
          <Button as="a" href="/login" appearance="primary" size="large">
            Sign in with Microsoft
          </Button>
        </div>
        <BrandFooter />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <nav className={styles.nav}>
        <BrandHome />
        <span className={styles.navDivider} />
        <Link to="/" className={styles.navLink}>Dashboards</Link>
        {isInternal && <Link to="/whoami" className={styles.navLink}>Who am I</Link>}
        {isInternal && <Link to="/admin" className={styles.navLink}>Admin</Link>}
        <span style={{ marginLeft: "auto" }}>
          <Body1>{user.userDetails}</Body1>
          {" · "}
          <a href="/logout">Sign out</a>
        </span>
      </nav>

      <Routes>
        <Route path="/" element={<Landing />} />
        {isInternal && <Route path="/whoami" element={<Whoami />} />}
        {isInternal && <Route path="/admin" element={<Admin />} />}
        <Route path="/403" element={<Forbidden />} />
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      <BrandFooter />
    </div>
  );
}
